import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { useContext, useEffect, useState } from "react";
import { ArrowLeftIcon, CheckCircleIcon, XCircleIcon } from "@heroicons/react/20/solid";
import { DefaultInput, DefaultSelect } from "../../../inputs/DefaultInput";
import { bankAccountOptionsFiltered, typeBankAccountOptionsLabel } from "../../../../utils/options_selects";
import { APIClient } from "../../../../helpers/api_helper";
import { ADD_PRINCIPAL_BANK_ACCOUNT } from "../../../../helpers/url_helper";
import { OfertaViewContext } from "../../../../context/OfertaContext/OfertaViewContext";
import { LoadingView } from "../../../loading/Loading";
import { sanitizarInputNumerico } from "../../../../utils/validateData";

const apiClient = new APIClient();
const timeOut = 2000;

export function AddBank() {
  const { updateMyBanks, myBanks } = useContext(OfertaViewContext);
  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [account, setAccount] = useState({
    id: "",
    bankAccount: "",
    typeBankAccount: "",
    accountNumber: "",
    parent: true,
  });
  const [parentAccount, setParentAccount] = useState({});
  const [errorField, setErrorField] = useState({
    bankAccount: "",
    typeBankAccount: "",
    accountNumber: "",
  });
  const [isError, setIsError] = useState(false);
  const [messageError, setMessageError] = useState("Error al agregar la cuenta, intenta luego.");
  const [isSuccess, setIsSuccess] = useState(false);


  const handleChangeBankAccount = (e) => {
    setAccount({
      ...account,
      bankAccount: e.target.value,
    });
  }

  const handleChangeTypeBankAccount = (e) => {
    setAccount({
      ...account,
      typeBankAccount: e.target.value,
    });
  }

  const handleChangeAccountNumber = (e) => {
    setAccount({
      ...account,
      accountNumber: e.target.value,
    });
  };

  const handleClose = () => {
    setOpen(false);
    setIsError(false);
    setIsSuccess(false);
    setMessageError("");
    clearInputs();
  };

  const validateInputs = () => {
    const newErrors = {};
    let hasValidate = true;
    let regexNumber = /^[0-9]+$/;
    if (account.bankAccount === "" || account.bankAccount === "0") {
      newErrors.bankAccount = "Debe seleccionar un banco.";
    } else if (account.typeBankAccount === "" || account.typeBankAccount === "Selecciona una opción") {
      newErrors.typeBankAccount = "Debe seleccionar un tipo de cuenta."
    } else if (account.accountNumber === "") {
      newErrors.accountNumber = "Debe digitar el número de cuenta.";
    } else if (!regexNumber.test(account.accountNumber)) {
      newErrors.accountNumber = "Debe ingresar un número válido.";
    }
    if (Object.keys(newErrors).length > 0) {
      setErrorField(newErrors);
      hasValidate = false;
    } else {
      setErrorField({});
    }
    return hasValidate;
  };

  const clearInputs = () => {
    setAccount({
      bankAccount: "",
      typeBankAccount: "",
      accountNumber: "",
      parent: true,
    });
    setErrorField({});
  };

  const addAccount = async () => {
    if (!validateInputs()) return;
    if (Array.isArray(myBanks.myBankAccounts) && myBanks.myBankAccounts.length >= 4) {
      setMessageError("Solo puedes agregar hasta 4 cuentas.");
      setIsError(true);
      setTimeout(() => {
        handleClose();
      }, timeOut);
      return;
    }
    try {
      setIsLoading(true);
      account.accountNumber = sanitizarInputNumerico(account.accountNumber);
      let payload = {
        documento: localStorage.getItem("doc"),
        bankAccount: account,
        idPrincipal: parentAccount?.id,
      }
      let { data } = await apiClient.create(ADD_PRINCIPAL_BANK_ACCOUNT, payload);
      let myBanksData = myBanks;
      let accountsUpdated = myBanksData.myBankAccounts.map((acc) => {
        return acc.id === parentAccount.id ? { ...acc, parent: false } : acc;
      });
      account.id = data?.id;
      account.bankAccount = bankAccountOptionsFiltered.filter((acc) => acc.value === Number(account?.bankAccount))[0]?.label;
      accountsUpdated.push(account);
      updateMyBanks({
        ...myBanks,
        myBankAccounts: accountsUpdated,
        parentAccount: account
      });
      setIsSuccess(true);
      setTimeout(() => {
        return handleClose();
      }, timeOut);
    } catch (error) {
      console.error(error);
      setIsError(true);
    } finally {
      setIsLoading(false);
      clearInputs();
    }
  }

  useEffect(() => {
    setParentAccount(myBanks?.parentAccount);
  }, [myBanks?.parentAccount]);

  return (
    <>
      <div onClick={myBanks.myBankAccounts.length >= 4 ? null : () => setOpen(true)}>
        <div className={`${myBanks.myBankAccounts.length >= 4 && "bg-tgray cursor-not-allowed"} bg-green text-white p-1 px-2 text-sm rounded-full cursor-pointer`}>
          + Agregar
        </div>
      </div>
      <Dialog open={open} onClose={setOpen} className="relative z-50">
        <DialogBackdrop
          transition
          className="fixed inset-0 bg-transparent transition-opacity duration-200 ease-in-out data-closed:opacity-0"
        />

        <div className="fixed inset-x-0 overflow-hidden">
          <div className="absolute inset-0 overflow-hidden">
            <div className="pointer-events-none fixed md:inset-y-0 bottom-0 md:right-0 flex max-w-full md:pl-10">
              <DialogPanel
                transition
                className="pointer-events-auto w-screen md:max-w-sm transform h-128 md:h-full transition duration-300 ease-in-out data-closed:translate-x-full"
              >
                <div className="flex h-full flex-col rounded-t-3xl md:rounded-none bg-darkBg tracking-tight font-walsheim">
                  <div>
                    <div className="py-6">
                      <div className="flex justify-between px-4 pt-2 md:pt-8">
                        <div className="flex h-7 items-center">
                          <button
                            type="button"
                            onClick={handleClose}
                            className="relative text-white focus:outline-hidden"
                          >
                            <span className="absolute" />
                            <span className="sr-only">Close panel</span>
                            <ArrowLeftIcon
                              aria-hidden="true"
                              className="h-6 w-6"
                            />
                          </button>
                        </div>
                        <DialogTitle>
                          <div className="text-white">
                            Agregar cuenta bancaria
                          </div>
                        </DialogTitle>
                        <div className="flex h-7 items-center">
                          <button
                            type="button"
                            onClick={handleClose}
                            className="relative text-white focus:outline-hidden"
                          >
                            <span className="absolute" />
                            <span className="sr-only">Close panel</span>
                            <XMarkIcon aria-hidden="true" className="h-6 w-6" />
                          </button>
                        </div>
                      </div>
                      <div className="pt-2 px-4 text-sm text-grayP">
                        Recuerda que debe estar a tu nombre para que el
                        desembolso sea exitoso
                      </div>
                    </div>
                  </div>
                  <div className="h-full grid content-between p-4 rounded-t-3xl bg-white overflow-auto">
                    <div className="lg:p-4 pt-4">
                      <div>
                        <h2 className="font-bold">Banco</h2>
                        <DefaultSelect
                          option={bankAccountOptionsFiltered}
                          onChange={handleChangeBankAccount}
                          value={account.bankAccount}
                        />
                        {
                          errorField?.bankAccount && (
                            <span className="text-red">{errorField?.bankAccount}</span>
                          )
                        }
                      </div>
                      <div className="pt-8">
                        <h2 className="font-bold">Tipo de cuenta</h2>
                        <DefaultSelect
                          option={typeBankAccountOptionsLabel}
                          onChange={handleChangeTypeBankAccount}
                          value={account.typeBankAccount}
                        />
                        {
                          errorField?.typeBankAccount && (
                            <span className="text-red">{errorField?.typeBankAccount}</span>
                          )
                        }

                      </div>
                      <div className="pt-8">
                        <h2 className="font-bold">Número de cuenta</h2>
                        <DefaultInput
                          onChange={handleChangeAccountNumber}
                          value={account.accountNumber}
                          type='number'
                        />
                        {
                          errorField?.accountNumber && (
                            <span className="text-red">{errorField?.accountNumber}</span>
                          )
                        }
                      </div>
                    </div>

                    <div>
                      {
                        isError && (
                          <>
                            <div className="flex justify-center py-2">
                              {" "}
                              <span className="text-sm text-red pt-2">
                                {messageError}
                              </span>{" "}
                            </div>
                            <div className="flex justify-center text-red py-2">
                              <XCircleIcon className="w-20 h-auto" />
                            </div>
                          </>
                        )
                      }
                      {
                        isSuccess && (
                          <>
                            <div className="flex justify-center py-2">
                              {" "}
                              <span className="text-sm text-green pt-2">
                                Cuenta agregada con éxito
                              </span>{" "}
                            </div>
                            <div className="flex justify-center text-green py-2">
                              <CheckCircleIcon className="w-20 h-auto" />
                            </div>
                          </>
                        )
                      }
                      {
                        isLoading && (
                          <LoadingView text={"Agregando cuenta..."} />
                        )
                      }
                      <button
                        disabled={isLoading || isError || isSuccess}
                        onClick={addAccount}
                        type="button"
                        className={`w-full flex justify-center py-2 px-4 border border-transparent rounded-full shadow-xs text-lg text-white font-semibold ${isLoading || isError || isSuccess ? "bg-tgray" : "bg-purple hover:bg-purpleDark hover:scale-100 transition duration-300  focus:outline-hidden"}`}
                      >
                        <div className="flex">
                          Guardar cuenta
                        </div>
                      </button>
                    </div>
                  </div>
                </div>
              </DialogPanel>
            </div>
          </div>
        </div>
      </Dialog>
    </>
  );
}
